import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";
import { apiClient } from "src/api/client";
import { ClientDetailResponse, AccountsListParams, AccountItem } from "src/api/types";
import { parseApiError } from "src/utils/errors/errorParser";
import { AxiosError } from "axios";

interface ClientDetailState {
  client: ClientDetailResponse | null;
  accounts: AccountItem[];
  accountsMeta: {
    total: number;
    page: number;
    page_size: number;
    pages: number;
  };
  loading: boolean;
  accountsLoading: boolean;
  accountsRequestId: string | null;
  accountsForClientId: number | null;
  error: string | null;
}

const initialState: ClientDetailState = {
  client: null,
  accounts: [],
  accountsMeta: {
    total: 0,
    page: 1,
    page_size: 20,
    pages: 0,
  },
  loading: false,
  accountsLoading: false,
  accountsRequestId: null,
  accountsForClientId: null,
  error: null,
};

export const fetchClientDetail = createAsyncThunk(
  "clientDetail/fetchClientDetail",
  async (linkId: number, { rejectWithValue }) => {
    try {
      const response = await apiClient.getClientDetail(linkId);
      console.log('response', response);
      return response;
    } catch (error) {
      const err = parseApiError(error as AxiosError);
      return rejectWithValue(err.message);
    }
  }
);

export const fetchClientAccounts = createAsyncThunk(
  "clientDetail/fetchClientAccounts",
  async (params: AccountsListParams, { rejectWithValue }) => {
    try {
      const response = await apiClient.getAccounts(params);
      return response;
    } catch (error) {
      const err = parseApiError(error as AxiosError);
      return rejectWithValue(err.message);
    }
  }
);

const clientDetailSlice = createSlice({
  name: "clientDetail",
  initialState,
  reducers: {
    clearClientDetail: (state) => {
      state.client = null;
      state.accounts = [];
      state.accountsMeta = initialState.accountsMeta;
      state.accountsLoading = false;
      state.accountsRequestId = null;
      state.accountsForClientId = null;
      state.error = null;
    },
    clearClientAccounts: (state) => {
      state.accounts = [];
      state.accountsMeta = initialState.accountsMeta;
      state.accountsLoading = false;
      state.accountsRequestId = null;
      state.accountsForClientId = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setClientLinkStatus: (state, action: PayloadAction<string>) => {
      if (state.client) {
        state.client.status = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClientDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.client = action.payload;
      })
      .addCase(fetchClientDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchClientAccounts.pending, (state, action) => {
        const requestedClientId = action.meta.arg.client_id ?? null;
        if (state.accountsForClientId !== requestedClientId) {
          state.accounts = [];
          state.accountsMeta = initialState.accountsMeta;
          state.accountsForClientId = null;
        }
        state.accountsLoading = true;
        state.accountsRequestId = action.meta.requestId;
        state.error = null;
      })
      .addCase(fetchClientAccounts.fulfilled, (state, action) => {
        if (state.accountsRequestId !== action.meta.requestId) return;
        state.accountsLoading = false;
        state.accounts = action.payload.items;
        state.accountsMeta = action.payload.meta;
        state.accountsForClientId = action.meta.arg.client_id ?? null;
        state.accountsRequestId = null;
      })
      .addCase(fetchClientAccounts.rejected, (state, action) => {
        if (state.accountsRequestId !== action.meta.requestId) return;
        state.accountsLoading = false;
        state.error = action.payload as string;
        state.accountsRequestId = null;
      });
  },
});

export const {
  clearClientAccounts,
  clearClientDetail,
  clearError,
  setClientLinkStatus,
} = clientDetailSlice.actions;
export default clientDetailSlice.reducer;
